import { RoundedButton } from '@/components/rounded-button';
import { getVaultMetadataByAddress } from '@/constants/nucleus-vaults';
import { wrappedPlumeSymbol } from '@/constants/shared';
import { useRoycoPositionDataForVaultKey } from '@/hooks/useRoycoPositionsData';
import { AmplitudeTrackingEvent, trackAmplitudeEvent } from '@/lib/amplitude';
import { getIconPathFromTokenSymbol, getTimeUntilUnlock, isTruthy } from '@/utils';
import { Card, FormatNumber, HStack, Icon, Image, Text } from '@chakra-ui/react';
import { prepareApproveWithdrawToken, prepareWithdrawTransactionData } from '@molecularlabs/nucleus-frontend';
import { TokenKey } from '@molecularlabs/nucleus-frontend/dist/vaults/tokens';
import { useAppKitAccount } from '@reown/appkit/react';
import { simulateContract, waitForTransactionReceipt } from '@wagmi/core';
import { useCallback, useMemo, useState } from 'react';
import { FiArrowRight, FiPlus } from 'react-icons/fi';
import { PiSealWarningFill } from 'react-icons/pi';
import { useNavigate, useSearchParams } from 'react-router';
import {
  claimRecipeIncentiveTokenTxOptions,
  forfeitRecipePositionTxOptions,
  withdrawRecipeInputTokenTxOptions,
} from 'royco/transaction';
import { useChainId, useConfig, useWaitForTransactionReceipt, useWriteContract } from 'wagmi';
import { DepositsAndWithdrawalFrame } from '../DepositAndWithdrawalFrame';
import { useCurrentVault } from '../hooks/useCurrentVault';
import { useDepositsAndWithdrawalsToasts } from '../hooks/useDepositsAndWIthdrawalsToasts';
import { StepsCard } from '../StepsCard';
import { TransactionStep } from '../TransactionStep';
import { useWithdrawToken } from './useWithdrawExchangeRate';

export const WithdrawSteps = () => {
  const wagmiConfig = useConfig();
  const { vaultKey, name, vaultAddress } = useCurrentVault();
  const { getSuccessToast, getErrorToast } = useDepositsAndWithdrawalsToasts();
  const { address } = useAppKitAccount();
  const chainId = useChainId();
  const [generalErrorMsg, setGeneralErrorMsg] = useState<string>();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { currentVaultRoycoPositionData } = useRoycoPositionDataForVaultKey({ vaultKey });

  const roycoPositionId = searchParams.get('royco_position_id');

  const currentRoycoPosition = currentVaultRoycoPositionData?.find(
    ({ id }) => id.toLowerCase() === roycoPositionId?.toLowerCase(),
  );

  const { unclaimedWPlumeIncentiveToken, id } = currentRoycoPosition ?? {};

  const { exchangeRate, tokenSymbol } = useWithdrawToken();

  const { fromAmount, toAmount } = useMemo(() => {
    const fromAmountFromParams = searchParams.get('from_amount');
    const toAmountFromParams = searchParams.get('to_amount');
    // from amount = # vault tokens to withraw. if this is specified, calculate withdraw token values
    // based on the amount of vault tokens we want to withdraw
    // this happens if we initiate a withdrawal by liquidating a royco position
    if (fromAmountFromParams) {
      return {
        fromAmount: Number(fromAmountFromParams),
        // exchangeRate is # withdraw tokens per vault share
        // withdrawTokensAmount = vaultTokensAmount * withdrawTokensAmount/vaultToken
        toAmount: Number(fromAmountFromParams) * exchangeRate,
      };
    }
    return {
      toAmount: Number(toAmountFromParams),
      // exchangeRate is # withdraw tokens per vault share
      // vaultTokensAmount =  withdrawTokensAmount / (withdrawTokensAmount/vaultToken)
      fromAmount: Number(toAmountFromParams) / exchangeRate,
    };
  }, [exchangeRate, searchParams]);

  const {
    writeContractAsync: approveVaultSpendingAsync,
    isPending: isPendingVaultApproval,
    isError: isVaultApprovalError,
  } = useWriteContract();

  const {
    writeContractAsync: withdrawFromVaultAsync,
    data: withdrawVaultTxHash,
    isError: isWithdrawVaultError,
    isPending: isPendingWithdrawVaultTx,
  } = useWriteContract();

  const { isLoading: isWaitingForVaultWithdrawTxReceipt } = useWaitForTransactionReceipt({
    hash: withdrawVaultTxHash,
  });

  const approveAndWithdrawFromVault = useCallback(async () => {
    try {
      const approveWithdrawData = await prepareApproveWithdrawToken({
        vaultKey,
        withdrawAmount: fromAmount.toString(),
        chainId,
      });
      await simulateContract(wagmiConfig, approveWithdrawData);
      await approveVaultSpendingAsync(approveWithdrawData);
      const withdrawData = await prepareWithdrawTransactionData({
        vaultKey: vaultKey,
        chainId,
        userAddress: address as EthAddress,
        wantTokenSymbol: tokenSymbol as TokenKey,
        offerAmount: fromAmount.toString(),
      });
      trackAmplitudeEvent(AmplitudeTrackingEvent.PortalOptimizerWithdrawAttempt, {
        vault_name: getVaultMetadataByAddress(vaultAddress)?.name || vaultAddress,
        withdraw_amount: String(fromAmount),
      });
      const hash = await withdrawFromVaultAsync(withdrawData);
      await waitForTransactionReceipt(wagmiConfig, { hash });
      trackAmplitudeEvent(AmplitudeTrackingEvent.PortalOptimizerWithdraw, {
        vault_name: getVaultMetadataByAddress(vaultAddress)?.name || vaultAddress,
        withdraw_amount: String(fromAmount),
      });
      getSuccessToast({
        actionName: 'vault withdrawal',
        destinationName: 'vault balance',
      });
    } catch (err) {
      console.error('Error preparing withdrawal:', err);
      setGeneralErrorMsg(`Failed to approve withdrawal: ${err}`);
    }
  }, [
    vaultKey,
    fromAmount,
    chainId,
    approveVaultSpendingAsync,
    address,
    tokenSymbol,
    withdrawFromVaultAsync,
    getSuccessToast,
  ]);

  const {
    writeContractAsync: writeForfeitContractAsync,
    data: forfeitTxHash,
    isError: isForfeitError,
  } = useWriteContract();
  const { writeContractAsync: writeClaimContractAsync, isError: isClaimError } = useWriteContract();
  const {
    writeContractAsync: writeWithdrawContractAsync,
    data: withdrawFromRoycoTxHash,
    isError: isWithdrawError,
  } = useWriteContract();

  const [isPendingForfeitReceipt, setIsPendingForfeitReceipt] = useState(false);
  const [isPendingClaimReceipt, setIsPendingClaimReceipt] = useState(false);
  const [isPendingRoycoWithdrawReceipt, setIsPendingRoycoWithdrawReceipt] = useState(false);

  const forfeitRoycoPosition = useCallback(async () => {
    if (!id) {
      setGeneralErrorMsg(`No royco position found to forfeit for id ${id}`);
      return;
    }
    try {
      setIsPendingForfeitReceipt(true);
      const txArgs = await forfeitRecipePositionTxOptions({
        chainId,
        weirollWallet: id,
      });
      if (!txArgs.length) {
        throw new Error('Error in forfeitPosition: No tx args returned from forfeitRecipePositionTxOptions');
      }
      const typedTxArgs = {
        ...txArgs[0],
        address: txArgs[0].address as EthAddress,
      };
      await simulateContract(wagmiConfig, typedTxArgs);
      const hash = await writeForfeitContractAsync(typedTxArgs);
      await waitForTransactionReceipt(wagmiConfig, {
        hash,
      });
      getSuccessToast({
        actionName: 'royco forfeit',
        destinationName: 'vault balance',
      });
    } catch (e) {
      console.error('Error when forfeiting position', e);
      setGeneralErrorMsg(`Transaction failed with the following error: ${e}`);
      getErrorToast({
        actionName: 'royco forfeit',
        errorMessage: String(e),
      });
    } finally {
      setIsPendingForfeitReceipt(false);
    }
  }, [chainId, getErrorToast, getSuccessToast, id, writeForfeitContractAsync]);

  const claimRoycoRewards = useCallback(async () => {
    if (!address || !unclaimedWPlumeIncentiveToken?.contractAddress || !id) {
      const missingAttributes = [
        !address && 'address',
        !unclaimedWPlumeIncentiveToken?.contractAddress && 'tokenAddress',
      ].filter(isTruthy);
      setGeneralErrorMsg(`Missing required data for claim: ${missingAttributes.join(', ')}`);
      return;
    }
    try {
      setIsPendingClaimReceipt(true);
      const txArgs = await claimRecipeIncentiveTokenTxOptions({
        chainId,
        weirollWallet: id,
        tokenAddress: unclaimedWPlumeIncentiveToken?.contractAddress,
        accountAddress: address,
      });
      if (!txArgs.length) {
        throw new Error('Error in claimPosition: No tx args returned from claimRecipeIncentiveTokenTxOptions');
      }
      const typedTxArgs = {
        ...txArgs[0],
        address: txArgs[0].address as EthAddress,
      };
      await simulateContract(wagmiConfig, typedTxArgs);
      const hash = await writeClaimContractAsync(typedTxArgs);
      await waitForTransactionReceipt(wagmiConfig, {
        hash,
      });
      getSuccessToast({
        actionName: 'royco rewards claim',
        destinationName: 'rewards balance',
      });
      return hash;
    } catch (e) {
      console.error('Error when claiming rewards', e);
      setGeneralErrorMsg(`Error claiming rewards: ${e}`);
      getErrorToast({
        actionName: 'royco rewards claim',
        errorMessage: String(e),
      });
    } finally {
      setIsPendingClaimReceipt(false);
    }
  }, [
    address,
    chainId,
    getErrorToast,
    getSuccessToast,
    id,
    unclaimedWPlumeIncentiveToken?.contractAddress,
    writeClaimContractAsync,
    wagmiConfig,
  ]);

  const withdrawRoycoPosition = useCallback(
    async ({ hasWithdrawRewards = false }: { hasWithdrawRewards?: boolean }) => {
      if (!id) {
        setGeneralErrorMsg(`No royco position found to withdraw for id ${id}`);
        return;
      }
      try {
        trackAmplitudeEvent(AmplitudeTrackingEvent.PortalOptimizerRoycoWithdrawAttempt, {
          market_name: getVaultMetadataByAddress(vaultAddress)?.name || vaultAddress,
          withdraw_amount: String(fromAmount),
          claimed_rewards: hasWithdrawRewards,
        });
        setIsPendingRoycoWithdrawReceipt(true);
        const txArgs = await withdrawRecipeInputTokenTxOptions({
          chainId,
          weirollWallet: id,
        });
        if (!txArgs.length) {
          throw new Error('Error in withdrawPosition: No tx args returned from withdrawRecipeInputTokenTxOptions');
        }
        const typedTxArgs = {
          ...txArgs[0],
          address: txArgs[0].address as EthAddress,
        };
        await simulateContract(wagmiConfig, typedTxArgs);
        const hash = await writeWithdrawContractAsync(typedTxArgs);
        await waitForTransactionReceipt(wagmiConfig, {
          hash,
        });
        trackAmplitudeEvent(AmplitudeTrackingEvent.PortalOptimizerRoycoWithdraw, {
          market_name: getVaultMetadataByAddress(vaultAddress)?.name || vaultAddress,
          withdraw_amount: String(fromAmount),
          claimed_rewards: hasWithdrawRewards,
        });
        getSuccessToast({
          actionName: 'royco withdrawal',
          destinationName: 'vault balance',
        });
      } catch (e) {
        console.error('Error when withdrawing position', e);
        setGeneralErrorMsg(`Error withdrawing from Royco position: ${e}`);
        getErrorToast({
          actionName: 'royco withdrawal',
          errorMessage: String(e),
        });
      } finally {
        setIsPendingRoycoWithdrawReceipt(false);
      }
    },
    [chainId, getErrorToast, getSuccessToast, id, writeWithdrawContractAsync, wagmiConfig],
  );

  // TODO: maybe combine these inner methods
  const claimAndWithdrawPosition = useCallback(async () => {
    const hash = await claimRoycoRewards();
    if (hash)
      await withdrawRoycoPosition({
        hasWithdrawRewards: true,
      });
  }, [claimRoycoRewards, withdrawRoycoPosition]);

  const forfeitComplete = !!forfeitTxHash && !isPendingForfeitReceipt;
  const claimAndWithdrawComplete = !!withdrawFromRoycoTxHash && !isPendingRoycoWithdrawReceipt;
  const withdrawFromVaultComplete = !!withdrawVaultTxHash && !isWaitingForVaultWithdrawTxReceipt;

  const cancelAction = useCallback(() => {
    navigate(`/optimizer/${vaultAddress}`);
  }, [navigate, vaultAddress]);

  const transactionSteps = useMemo(() => {
    const steps = [];
    if (currentRoycoPosition) {
      const unclaimedWPlume = currentRoycoPosition.unclaimedWPlumeIncentiveToken;
      if (unclaimedWPlume && !unclaimedWPlume?.isUnlocked) {
        // is not unlocked, give user the forfeit action step
        steps.push({
          icon: '/assets/vaults/royco-inverted.svg',
          title: 'Withdraw from Royco Market',
          complete: forfeitComplete,
          loading: isPendingForfeitReceipt,
          started: true,
          slotRight: (
            <Text color="#F4801A" fontSize="sm">
              Incentives unlock in {getTimeUntilUnlock(Number(unclaimedWPlume.unlockTimestamp))}
            </Text>
          ),
          overview: (
            <HStack>
              <Image src={getIconPathFromTokenSymbol(vaultKey ?? '')} boxSize={5} />
              <FormatNumber value={fromAmount} />
              <Text color="fg.muted">{vaultKey}</Text> <FiPlus />{' '}
              <Image src={getIconPathFromTokenSymbol(wrappedPlumeSymbol)} boxSize={5} />
              <Text textDecorationLine="line-through">
                <FormatNumber value={unclaimedWPlume.tokenAmount} />{' '}
                <Text as="span" color="fg.muted">
                  {wrappedPlumeSymbol}
                </Text>
              </Text>
            </HStack>
          ),
          ctas: [{ action: forfeitRoycoPosition, name: 'Withdraw From Royco & Forfeit Incentives', variant: 'solid' }],
        });
      }
      if (unclaimedWPlume && unclaimedWPlume.isUnlocked) {
        // claim + withdraw, give user the claim + withdraw action step
        steps.push({
          icon: '/assets/vaults/royco-inverted.svg',
          title: 'Withdraw from Royco Market',
          // TODO: use vault methods, not royco methods
          complete: claimAndWithdrawComplete,
          loading: isPendingClaimReceipt || isPendingRoycoWithdrawReceipt,
          started: true,
          overview: (
            <HStack>
              <Image src={getIconPathFromTokenSymbol(vaultKey ?? '')} boxSize={5} />
              <FormatNumber value={fromAmount} />
              <Text color="fg.muted">{vaultKey}</Text> <FiPlus />{' '}
              <Image src={getIconPathFromTokenSymbol(wrappedPlumeSymbol)} boxSize={5} />
              <FormatNumber value={unclaimedWPlume.tokenAmount} /> <Text color="fg.muted">{wrappedPlumeSymbol}</Text>
            </HStack>
          ),

          ctas: [
            { action: claimAndWithdrawPosition, name: 'Withdraw From Royco & Claim Incentives', variant: 'solid' },
          ],
        });
      }
    }

    // always give user the vault withdraw action step
    steps.push({
      icon: '/assets/vaults/yield-optimizer.svg',
      title: `Withdraw from ${name}`,
      complete: withdrawFromVaultComplete,
      loading:
        (isPendingVaultApproval && !isVaultApprovalError) ||
        (isPendingWithdrawVaultTx && !isWithdrawVaultError) ||
        isWaitingForVaultWithdrawTxReceipt,
      started: forfeitComplete || claimAndWithdrawComplete || !currentRoycoPosition,
      overview: (
        <HStack>
          <Image src={getIconPathFromTokenSymbol(vaultKey ?? '')} boxSize={5} />
          <FormatNumber value={fromAmount} />
          <Text color="fg.muted">{vaultKey}</Text> <FiArrowRight />{' '}
          <Image src={getIconPathFromTokenSymbol(tokenSymbol ?? '')} boxSize={5} />
          <FormatNumber value={toAmount} /> <Text color="fg.muted">{tokenSymbol}</Text>
        </HStack>
      ),
      ctas: [
        currentRoycoPosition && {
          action: cancelAction,
          name: 'Withdraw Later',
          variant: 'outline',
        },
        {
          action: approveAndWithdrawFromVault,
          name: 'Withdraw From Optimizer',
        },
      ].filter(isTruthy),
    });
    return steps;
  }, [
    approveAndWithdrawFromVault,
    cancelAction,
    claimAndWithdrawComplete,
    claimAndWithdrawPosition,
    currentRoycoPosition,
    forfeitComplete,
    forfeitRoycoPosition,
    fromAmount,
    isWaitingForVaultWithdrawTxReceipt,
    isPendingClaimReceipt,
    isPendingForfeitReceipt,
    isPendingRoycoWithdrawReceipt,
    isPendingVaultApproval,
    isPendingWithdrawVaultTx,
    isVaultApprovalError,
    isWithdrawVaultError,
    name,
    toAmount,
    tokenSymbol,
    vaultKey,
    withdrawFromVaultComplete,
  ]);

  const error = generalErrorMsg || isForfeitError || isClaimError || isWithdrawError;
  const currentStep = useMemo(() => transactionSteps.find(({ complete }) => !complete), [transactionSteps]);
  const Actions = () => {
    if (!currentStep)
      return (
        <RoundedButton h={16} onClick={cancelAction} flex={1}>
          Complete
        </RoundedButton>
      );
    return currentStep.ctas.map(({ action, name, variant }) => (
      // TODO: get rid of typecasting by making proper types for transactionsteps
      <RoundedButton
        onClick={action}
        h={16}
        flex={1}
        variant={variant as 'solid' | 'outline'}
        loading={currentStep.loading}
      >
        {name}
      </RoundedButton>
    ));
  };
  return (
    <DepositsAndWithdrawalFrame
      title={`Withdraw from ${name}`}
      description="Royco positions must be exited before you can withdraw from the Optimizer"
    >
      <StepsCard title="Withdraw Steps" description="Do not close this window when the transaction is in progress">
        {unclaimedWPlumeIncentiveToken?.isUnlocked === false && (
          <Card.Root borderRadius="2xl" bgColor="#F4801A1A" border="none">
            <Card.Body>
              <HStack gap={4}>
                <Icon color="#F4801A" boxSize={5}>
                  <PiSealWarningFill />
                </Icon>
                <Text fontSize="sm" fontWeight="500">
                  You are giving up on{' '}
                  <FormatNumber
                    value={currentRoycoPosition?.unclaimedWPlumeIncentiveToken?.tokenAmount ?? 0}
                    maximumFractionDigits={2}
                  />{' '}
                  PLUME incentives{' '}
                  <Text as="span" color="fg.muted">
                    by withdrawing before the end of the forfeitable period
                  </Text>
                </Text>
              </HStack>
            </Card.Body>
          </Card.Root>
        )}
        {transactionSteps.map((transactionStep, idx) => (
          <TransactionStep key={transactionStep.title} stepNumber={idx + 1} {...transactionStep} />
        ))}
        <HStack>
          <Actions />
        </HStack>
        {error && (
          <Card.Root>
            <Card.Body>Error {error}</Card.Body>
          </Card.Root>
        )}
      </StepsCard>
    </DepositsAndWithdrawalFrame>
  );
};
